import React, { useState, useContext, useEffect, useRef } from 'react';
import { Form, Radio, Input, Button,notification } from 'antd';
import { NumericFormat } from 'react-number-format';
import axios from 'axios';
import io from 'socket.io-client';
import './AirtimeTopup.css';
import { AuthContext } from '../context/AuthContext';
import config from '../config.json';

const layout = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 18,
  },
};

const tailLayout = {
  wrapperCol: {
    offset: 0,
    span: 24,
    textAlign: 'center',
  },
};

function AirtimeTopup() {
  const [form] = Form.useForm();
  const [phoneNumberDisabled, setPhoneNumberDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const { user } = useContext(AuthContext);
  const socketRef = useRef(null);
  const lastStatusRef = useRef(null);
  const currentRequestRef = useRef(null);

  const formatPhoneNumber = (phone) => {
    if (phone.startsWith('211') && phone.length === 12) {
      return `+${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
    }
    return phone;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  useEffect(() => {
    console.log('user:', user);
    if (user && user.whatsappNumber && form.getFieldValue('topupFor') === 'myself') {
      form.setFieldsValue({
        phoneNumber: user.whatsappNumber,
      });
    }
  }, [user, form]);

  useEffect(() => {
    console.log('User in socket effect:', user);
    if (!socketRef.current) {
      socketRef.current = io(config.API_URL);
      socketRef.current.emit('join', user.whatsappNumber);
      socketRef.current.on('connect', () => {
        console.log('Socket connected');
        console.log('Emitting join for user:', user.whatsappNumber);
        socketRef.current.emit('join', user.whatsappNumber);
      });
      socketRef.current.on('requestStatusUpdate', (data) => {
        console.log('Received requestStatusUpdate:', data);
        console.log('Current requestId:', currentRequestRef.current?.requestId, 'received:', data.requestId);
        console.log('currentRequest:', currentRequestRef.current);
        if (currentRequestRef.current && data.requestId === currentRequestRef.current.requestId) {
          console.log('Updating status to:', data.status);
          setCurrentRequest({ ...currentRequestRef.current, status: data.status });
          if (data.status !== lastStatusRef.current) {
            lastStatusRef.current = data.status;
            if (data.status === 'Completed') {
              notification.success({
                message: 'Request Completed',
                description: 'Your airtime topup request has been completed successfully.',
                placement: 'topRight',
              });
            } else if (data.status === 'Failed') {
              notification.error({
                message: 'Request Failed',
                description: 'Your airtime topup request has failed. Please try again.',
                placement: 'topRight',
              });
            } else {
              notification.info({
                message: 'Request Status Updated',
                description: `Your request status is now: ${data.status}`,
                placement: 'topRight',
              });
            }
          }
        }
      });
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user]);

  useEffect(() => {
    currentRequestRef.current = currentRequest;
  }, [currentRequest]);

  const onTopupForChange = (e) => {
    const topupFor = e.target.value;
    setPhoneNumberDisabled(topupFor === 'myself');
    form.setFieldsValue({
      phoneNumber: topupFor === 'myself' && user ? user.whatsappNumber : '',
    });
  };

  const onFinish = async (values) => {
    setLoading(true);
    const { phoneNumber, amount } = values;
    const { active_accesskey, whatsappNumber } = user;

    const payload = {
      active_accesskey: active_accesskey,
      user_id: whatsappNumber,
      phoneNo: phoneNumber,
      amount: parseFloat(amount.replace(/,/g, '')),
    };

    try {
      const apiUrl = `${config.API_URL}/request/createRequest`;
      const response = await axios.post(apiUrl, payload);

      console.log('API Response:', response.data);

      if (response.data.status === false) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to top up airtime. Please try again.',
          placement: 'topRight',
        });
      } else {
        notification.warning({
          message: 'Request Submitted',
          description: 'Your airtime topup request has been submitted and is currently in pending state.',
          placement: 'topRight',
        });
        setCurrentRequest(response.data.data);
        form.resetFields(['amount', 'phoneNumber']);
        if(form.getFieldValue('topupFor') === 'myself'){
           form.setFieldsValue({
           phoneNumber: user.whatsappNumber,
         });
        }
       
      }
    } catch (error) {
      console.error('API Error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to top up airtime. Please try again.';
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="airtime-topup-container">
      <h1>Airtime Topup</h1>
      <Form
        {...layout}
        form={form}
        name="airtimeTopupForm"
        className="airtime-topup-form"
        initialValues={{
          topupFor: 'myself',
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item label="Topup For" name="topupFor">
          <Radio.Group onChange={onTopupForChange}>
            <Radio value="myself">Myself</Radio>
            <Radio value="others">Others</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Phone Number"
          name="phoneNumber"
          rules={[
            {
              required: true,
              message: 'Please input phone number!',
            },
            {
              pattern: /^[+]?\d+$/,
              message: 'Please enter a valid phone number!',
            },
          ]}
        >
          <Input
            placeholder="Enter phone number"
            disabled={phoneNumberDisabled}
            type="number"
          />
        </Form.Item>

        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            {
              required: true,
              message: 'Please input amount!',
            },
          ]}
        >
          <NumericFormat
            placeholder="Enter amount"
            // type="number"
            thousandSeparator=","
            // prefix="SSP "
            decimalScale={2}
            customInput={Input}
          />
        </Form.Item>

        <Form.Item {...tailLayout}>
          <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
            {loading ? 'Loading...' : 'Submit'}
          </Button>
        </Form.Item>
      </Form>
      {currentRequest && (
        <div className="current-request">
          <h3>Current Request Status</h3>
          {console.log('Rendering with status:', currentRequest.status)}
          <p><strong>Phone Number:</strong> {formatPhoneNumber(currentRequest.phoneNo)}</p>
          <p><strong>Amount:</strong> {formatAmount(currentRequest.amount)} SSP</p>
          <p><strong>Status:</strong> <span style={{ color: currentRequest.status === 'Completed' ? 'green' : currentRequest.status === 'Active' ? 'orange' : 'red', fontWeight: 'bold' }}>{currentRequest.status}</span></p>
        </div>
      )}
    </div>
  );
}

export default AirtimeTopup;
